import { useState } from 'react';
import Spinner from '../../components/Spinner';
import { fosApi, fosError, formatDate, isVersionConflict, newRequestId, type DonorDetail, type PersonCandidate } from '../../lib/fosApi';
import { Avatar, ErrorNote, Icon, Section } from './ui';

/** Optional, confirmed link between this donor and a Compass Person. Linking never merges or changes either record. */
export default function PersonLinkPanel({ detail, onChanged }: { detail: DonorDetail; onChanged: () => void }) {
  const { donor, permissions } = detail;
  const [candidates, setCandidates] = useState<{ visible: PersonCandidate[]; hiddenCount: number } | null>(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label); setError('');
    try { await fn(); onChanged(); setCandidates(null); }
    catch (cause) { const c = fosError(cause); setError(isVersionConflict(c) ? 'This donor changed. Reload the page and try again.' : c.message); }
    finally { setBusy(''); }
  };
  const find = async () => {
    setBusy('find'); setError('');
    try { setCandidates(await fosApi.personCandidates({ donorId: donor.id })); } catch (cause) { setError(fosError(cause).message); } finally { setBusy(''); }
  };
  const link = donor.personLink;

  return (
    <Section title="Compass person" icon="link">
      {link ? (
        <>
          {link.person ? (
            <div className="fos-person-card">
              <Avatar name={link.person.name} size={40} />
              <div>
                <strong>{link.person.name}</strong>
                <div className="subtitle">{link.person.ref ?? ''}{link.person.positions.length ? ` · ${link.person.positions.join(', ')}` : ''}</div>
                <div className="fos-chips">
                  <span className={`fos-chip ${link.person.status === 'active' ? 'healthy' : 'attention'}`}>{link.person.status === 'active' ? 'Active in Compass' : `Compass status: ${link.person.status}`}</span>
                  <span className={`fos-chip ${link.person.registration === 'Registered' ? 'healthy' : 'neutral'}`}>{link.person.registration}</span>
                </div>
              </div>
            </div>
          ) : <p className="fos-hint"><Icon name="lock" size={13} /> Linked to a Compass person you do not have access to see.</p>}
          <p className="fos-hint">Confirmed {formatDate(link.confirmedAt)}. This donor stays in Friends of Scouting whatever happens to the person’s registration.</p>
          {permissions.linkPerson && <button className="btn small" disabled={!!busy} onClick={() => run('unlink', () => fosApi.personLink({ requestId: newRequestId(), operation: 'unlink', donorId: donor.id, expectedVersion: donor.version }))}>{busy === 'unlink' ? <Spinner /> : 'Remove link'}</button>}
        </>
      ) : (
        <>
          <p className="fos-hint">Not linked. Link only when you are sure this donor is the same individual as someone in Compass; nothing is merged.</p>
          {permissions.linkPerson && !candidates && <button className="btn small" onClick={find} disabled={!!busy}>{busy === 'find' ? <Spinner /> : 'Look for a match'}</button>}
        </>
      )}
      {candidates && (
        <div className="fos-candidates">
          {candidates.visible.length === 0 && <p className="fos-hint">No likely matches among the people you can see.</p>}
          {candidates.visible.map((c) => (
            <div key={c.personId} className="fos-candidate">
              <Avatar name={c.displayName} size={30} />
              <div><strong>{c.displayName}</strong><div className="subtitle">Matches on {c.reasons.map((r) => r.replace('_', ' ')).join(', ')}</div></div>
              <button className="btn small primary" disabled={!!busy} onClick={() => run(c.personId, () => fosApi.personLink({ requestId: newRequestId(), operation: 'link', donorId: donor.id, expectedVersion: donor.version, personId: c.personId, confirm: true }))}>
                {busy === c.personId ? <Spinner /> : 'This is them'}
              </button>
            </div>
          ))}
          {candidates.hiddenCount > 0 && <p className="fos-hint">{candidates.hiddenCount} other possible match{candidates.hiddenCount === 1 ? '' : 'es'} outside your access.</p>}
          <button className="linkbtn" onClick={() => setCandidates(null)}>Close</button>
        </div>
      )}
      <ErrorNote message={error} />
    </Section>
  );
}
